
import {
  Component,
  Mixins,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Data
import { DataModules } from "@/store/modules/data";
import { QUERY_PARAMS } from "@/data/constants";
import { ClientRoutes } from "@/data/enums/router";

// Types
import { IState } from "@/store";
import { IMigrationProviderMethods } from "@/models/providers/migrations";
import { IProduct, IProductMigration } from "@/models/products";
import { IInvoice } from "@/models/invoices";
import { IAccount } from "@/models/accounts";

@Component({
  computed: {
    ...mapState({
      accountId: (state: IState) => state.auth.client.accountId
    })
  }
})
export default class MigrationProvider extends Mixins(RequestPromises) {
  // State
  public accountId!: IAccount["id"];

  @Provide()
  public get migrationMethods(): IMigrationProviderMethods {
    return {
      openMigrationListModal: this.openMigrationListModal,
      getMigrations: this.getMigrations
    };
  }

  @Prop({ required: true })
  public contract!: any;

  @Prop({ required: true })
  public contractProduct!: any;

  public getMigrations(limit = 4, offset = 0) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/list`, {
      returnData: false,
      storeData: false,
      clientApi: true,
      params: {
        limit,
        offset,
        account_id: this.accountId,
        currency_id: this.contract.currency_id,
        "filter[billing_cycle_months|neq]": 0,
        "filter[available_for_sales]": 1,
        "filter[clients_can_order]": 1,
        "filter[id|like]": this.allowedMigrationsIds,
        order: "order",
        with: ["image"].join()
      },
      vm: this
    });
  }

  public async openMigrationListModal() {
    const modal = await this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/client/contractProducts/migrations/migrationsListModal.vue"
          ),
        canCancel: ["escape", "outside", "x"],
        width: 1080,
        parent: this,
        props: {
          migrationMethods: this.migrationMethods
        },
        events: {
          close: () => {
            modal.close();
          },
          migrateProduct: (migrationId: IProduct["id"]) => {
            this.$router.push({
              query: { [QUERY_PARAMS.MIGRATION_ID]: migrationId }
            });
            modal.close();
          }
        }
      }
    });
  }

  public async openUpgradeDowngradeModal(migrationProductId: string) {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS_PRODUCTS}/openUpgradeDowngradeModal`,
      {
        modalConfig: {
          props: {
            contract: this.contract,
            contractProduct: this.contractProduct,
            migrationProductId
          },
          events: {
            success: (invoice: IInvoice) => {
              this.$router.push({
                name: ClientRoutes.INVOICE,
                params: {
                  invoiceId: invoice.id
                }
              });
            }
          }
        }
      }
    );
  }

  public get allowedMigrations(): IProductMigration[] {
    return this.contractProduct.allowed_migrations;
  }

  public get allowedMigrationsIds() {
    return this.$_.map(this.allowedMigrations, migration =>
      this.$_.get(migration, "migration_product_id", "")
    ).join(",");
  }

  public removeQueryParams(params) {
    this.$router.replace({
      query: this.$_.omit(this.$route.query, params)
    });
  }

  @Watch("$route.query", { immediate: true, deep: true })
  public onRouteQueryChange(query) {
    if (
      !this.$_.has(query, QUERY_PARAMS.MIGRATION_LIST) &&
      !this.$_.has(query, QUERY_PARAMS.MIGRATION_ID)
    )
      return;

    // Open migration lists modal
    if (this.$_.has(query, QUERY_PARAMS.MIGRATION_LIST))
      this.openMigrationListModal();

    // Open migrate modal
    if (this.$_.has(query, QUERY_PARAMS.MIGRATION_ID))
      this.openUpgradeDowngradeModal(
        this.$_.get(query, QUERY_PARAMS.MIGRATION_ID)
      );

    // Remove query params
    this.removeQueryParams([
      QUERY_PARAMS.MIGRATION_LIST,
      QUERY_PARAMS.MIGRATION_ID
    ]);
  }

  // Render Function
  public render() {
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $migrationMethods: this.migrationMethods
      })
    );
  }
}
